import { EventCodes } from '../../App/model/event-codes';
import type { EventService } from '../../App/services/event-service';
import type { IComponentController, IOnChangesObject, IAugmentedJQuery, ITimeoutService, IScope } from 'angular';
import template from './dashboard-widget.component.html';

import type { WidgetInstance, DashboardService } from '../dashboard.service';
import { DashboardPageController, DashboardConfigurationMode } from '../dashboard-page/dashboard-page.component';
import { SearchGroup } from '../../Shell/PandoraSolutionBuilderAgent/model/search-query/search-form.contracts';

export class DashboardWidgetController implements IComponentController {
	public widgetInstance!: WidgetInstance;
	public serviceContextInfo?: { widgetId: string; viewName: string };
	public page!: DashboardPageController;
	public internalTemplate = '';
	public template?: string;
	public onTitleClick!: (params: { widgetInstance: WidgetInstance }) => void;
	public onSelectItem!: (params: { widgetInstance: WidgetInstance }) => void;
	public fullscreen = false;

	static readonly $inject = ['$scope', '$element', 'mx.sb.Dashboard.Service', '$timeout', 'mx.shell.EventService'];

	constructor(
		private readonly $scope: IScope,
		private readonly $element: IAugmentedJQuery,
		private readonly dashboardService: DashboardService,
		private readonly $timeout: ITimeoutService,
		private readonly eventService: EventService
	) {}

	$onInit() {
		this.setServiceContext(this.widgetInstance);
	}

	$postLink(): void {
		const triggerDrilldownEvent = () => {
			this.$timeout(() => {
				if (this.hasDrilldown()) {
					this.onSelectItem({ widgetInstance: this.widgetInstance });
				}
			});
		};

		this.$element.on('click', '.highcharts-point', triggerDrilldownEvent);
		this.$element.on('left-click', 'nova-pie-chart', triggerDrilldownEvent);

		this.$element.on('keydown', (event) => {
			if (this.fullscreen && event.key === 'Escape') {
				event.stopPropagation();
				this.$scope.$evalAsync(() => {
					this.toggleFullscreen();
				});
			}
		});
	}

	$onChanges(onChangesObj: IOnChangesObject) {
		if (onChangesObj.widgetInstance || onChangesObj.template) {
			this.internalTemplate = this.template || '';
			this.setServiceContext(this.widgetInstance);
		}
	}

	public get dataModel() {
		return this.widgetInstance?.dataModel;
	}

	private setServiceContext(widgetInstance: WidgetInstance) {
		this.serviceContextInfo = {
			widgetId: widgetInstance.widgetId ?? '',
			viewName: widgetInstance.templateData?.widgetViewKey ?? '',
		};
	}

	public hasDrilldown() {
		return (this.page.configurationMode === DashboardConfigurationMode.Disabled || this.page.embeddedMode) && this.dashboardService.hasDrillDown(this.widgetInstance);
	}

	public getWidgetCategoryClassName() {
		return `mx-dashboard-widget__category-${this.dashboardService.categoryIdAlias(this.widgetInstance?.categoryId)}`;
	}

	public getWidgetClassName() {
		return `mx-dashboard-widget___${this.dashboardService.widgetIdAlias(this.widgetInstance?.widgetId)}`;
	}

	public handleTitleClick() {
		this.dashboardService.unsetSelectedItem(this.widgetInstance);
		this.onTitleClick({ widgetInstance: this.widgetInstance });
	}

	public hasAppliedFilters() {
		const hasAppliedFilterGroup = (filterGroup: SearchGroup | undefined) => {
			return Boolean(filterGroup?.Conditions?.length) || filterGroup?.Groups?.some(hasAppliedFilterGroup);
		};
		const ctx = this.widgetInstance.dataModel?.Context;
		return Boolean(ctx?.KeywordFilter) || hasAppliedFilterGroup(ctx?.DashboardFilters);
	}

	public onChangeLayout() {
		this.dashboardService.changeWidgetInstanceLayout(this.widgetInstance);
	}

	public toggleFullscreen() {
		this.fullscreen = !this.fullscreen;
		this.eventService.dispatchEvent(EventCodes.LayoutChanged, 'sideLayoutResized');
	}
}

export const DashboardWidgetComponent = {
	name: 'mxDashboardWidget',
	controller: DashboardWidgetController,
	controllerAs: 'vm',
	template,
	bindings: {
		widgetInstance: '<',
		template: '<',
		onEdit: '&',
		onRemove: '&',
		onTitleClick: '&',
		onSelectItem: '&',
		page: '<',
	},
};
